import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '@/utils/i18n';
import moment, { Moment } from 'moment';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ApolloQueryResult,
  gql,
  NetworkStatus,
  useMutation,
} from '@apollo/client';
import {
  AutopayContainer_SelectAutoPaySettingsMutation,
  AutopayContainer_SelectAutoPaySettingsMutationVariables,
  AutoPaySettingsFieldsFragment,
  AvailableCreditMemoListFieldsFragment,
  Fnti_UnlockExpiredFtniSessionsMutation,
  Fnti_UnlockExpiredFtniSessionsMutationVariables,
  PaymentForm_RemovePaymentMethodMutation,
  PaymentForm_RemovePaymentMethodMutationVariables,
  Payments_PaymentContextQuery,
  Payments_PaymentContextQueryVariables,
  Payments_PaymentsContextUnpaidListQuery,
  Payments_PaymentsContextUnpaidListQueryVariables,
  RestaurantAddressFieldsFragment,
  UnpaidInvoiceListFieldsFragment,
} from '@/types';
import {
  buildTransactionFeeMap,
  TransactionFeeMap,
} from '@/utils/transaction-fees';
import { captureException } from '@/utils/sentry';
import { DEFAULT_CURRENCY_CODE, getCountryCurrencyCode } from '@/utils/money';
import { formatSummaryDateWithYear } from '@/utils/date';
import { getOperationName } from 'apollo-utilities';
import { getTrueTime } from '@/utils/true-time';
import { PaymentItemType, PaymentMethodType } from '@/enums';
import { useAuthenticatedUserContext } from './user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export interface PaymentContextState {
  autoShowModal: boolean;
  memo: string;
  selectedInvoiceUUIDs: Set<string>;
  selectedPaymentMethod: PaymentMethodOption | null;
}

interface BaseUnpaidItemType {
  amountInMicros: number;
  dateText: string | null;
  displayNumber: string;
  uuid: string;
}

interface CreditMemoUnpaidItemType extends BaseUnpaidItemType {
  creditedAt: Date | null;
  type: PaymentItemType.Credit;
}

export interface InvoiceUnpaidItemType extends BaseUnpaidItemType {
  dueAt: Date | null;
  orderFulfillmentAt: Date | null;
  orderUUID: string | null;
  type: PaymentItemType.Invoice;
}

export type UnpaidItemType = CreditMemoUnpaidItemType | InvoiceUnpaidItemType;

export interface PaymentMethodOption {
  adyenPaymentID: string;
  adyenPaymentMethodJSON?: string;
  cashbackPercentage?: number;
  displayText: string;
  isDefault: boolean;
  isUnsaved: boolean;
  subtype: string;
  type: PaymentMethodType;
  uuid: string;
}

export interface AdyenConfigType {
  clientKey: string;
  countryCode: string;
  environment: string;
  paymentMethodsJson: string;
}

export type AutoPaySetting = Pick<
  AutoPaySettingsFieldsFragment,
  | 'frequency'
  | 'is_visible_to_restaurants'
  | 'max_amount_in_micros'
  | 'pay_based_on'
  | 'uuid'
>;

export interface PaymentsContextType {
  allUnpaidItems: UnpaidItemType[];
  autoPayEnabled: boolean;
  autoPaySelfServeEnabled: boolean;
  autoShowModal: boolean;
  chatAutoPaySetting: AutoPaySetting | null;
  countryCode: string | null;
  currencyCode: string | null;
  deselectAllUnpaidItems: () => void;
  enabledPaymentMethods: PaymentMethodType[];
  ftniEnabled: boolean;
  hasNewInvoices: boolean;
  isGrossUpTransactionFee: boolean;
  loading: boolean;
  markInvoicesViewed: (invoiceUUIDs?: string[]) => Promise<void>;
  memo: string;
  paymentMethods: PaymentMethodOption[];
  plaidEnabled: boolean;
  refetchPaymentContext: () => Promise<
    ApolloQueryResult<Payments_PaymentContextQuery>
  >;
  refreshing: boolean;
  refreshInvoices: () => Promise<void>;
  removePaymentMethod: (paymentMethodUUID: string) => Promise<void>;
  resetContext: () => void;
  restaurantAddress: RestaurantAddressFieldsFragment | null;
  restaurantsWithNewInvoices: Set<string>;
  savingAutoPaySetting: boolean;
  selectAllUnpaidItems: () => void;
  selectAutoPaySettings: (autoPaySettingUUID: string) => Promise<void>;
  selectedInvoiceUUIDs: Set<string>;
  selectedPaymentMethod: PaymentMethodOption | null;
  setPaymentContextState: (value: Partial<PaymentContextState>) => void;
  showChargebackDisclaimer: boolean;
  supplierAutoPaySettings: AutoPaySetting[];
  transactionFeeMap: TransactionFeeMap;
  unlockExpiredFtniInvoices: () => Promise<void>;
  useV2Invoices: boolean;
}

const PAYMENT_METHOD_FIELDS_FRAGMENT = gql`
  fragment PaymentMethodFields on payment_methods {
    display_text
    is_default
    payment_id
    payment_method_type
    payment_method_sub_type
    uuid
  }
`;

const ADDRESS_FIELDS_FRAGMENT = gql`
  fragment RestaurantAddressFields on addresses {
    city
    postal_code
    region
    route
    street_number
    uuid
  }
`;

export const PAYMENT_CONTEXT_QUERY = gql`
  fragment SupplierPaymentFields on businesses {
    address {
      country_code
      uuid
    }
    supplier_chats(
      limit: 1
      where: { restaurant_uuid: { _eq: $restaurantUUID } }
    ) {
      chat_auto_pay_settings(order_by: { created_at: desc })
        @skip(if: $skipInvoiceAndAutoPayData) {
        auto_pay_settings {
          ...AutoPaySettingsFields
        }
      }
      dynamic_config
      flat_transaction_fees
      restaurant {
        address {
          ...RestaurantAddressFields
        }
        uuid
      }
      transaction_fees
      uuid
    }
    supplier_metadata {
      dynamic_config
      flat_transaction_fees
      gross_up_transaction_fees_cutoff
      show_chargeback_disclaimer @skip(if: $skipInvoiceAndAutoPayData)
      transaction_fees
      use_v2_invoices @skip(if: $skipInvoiceAndAutoPayData)
      uuid
    }
    uuid
  }

  fragment AutoPaySettingsFields on auto_pay_settings {
    frequency
    is_visible_to_restaurants
    max_amount_in_micros
    pay_based_on
    uuid
  }

  query Payments_PaymentContext(
    $paymentMethodWhere: payment_methods_bool_exp!
    $restaurantUUID: uuid!
    $skipInvoiceAndAutoPayData: Boolean!
    $supplierUUID: uuid!
  ) {
    businesses_by_pk(uuid: $supplierUUID) {
      ...SupplierPaymentFields
    }

    auto_pay_settings(
      order_by: [{ created_at: desc }]
      where: { supplier_uuid: { _eq: $supplierUUID } }
    ) @skip(if: $skipInvoiceAndAutoPayData) {
      ...AutoPaySettingsFields
    }

    payment_methods(
      order_by: [{ is_default: desc }, { created_at: desc }]
      where: $paymentMethodWhere
    ) {
      ...PaymentMethodFields
    }
  }

  ${ADDRESS_FIELDS_FRAGMENT}
  ${PAYMENT_METHOD_FIELDS_FRAGMENT}
`;

export const PAYMENTS_CONTEXT_UNPAID_LIST_QUERY = gql`
  fragment AvailableCreditMemoListFields on CreditMemo {
    credit_amount_in_micros
    credit_date
    credit_id
    credit_number
  }

  fragment UnpaidInvoiceListFields on OrderInvoice {
    currency_code
    due_at
    invoice_id
    invoice_number
    order_fulfillment_at
    order_id
    unpaid_amount_in_micros
  }

  query Payments_PaymentsContextUnpaidList(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $invoiceRestaurantFilter: uuid
    $usableAfter: String
  ) {
    getAvailableCreditMemosByCustomer(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      usable_after: $usableAfter
    ) {
      credit_memos {
        ...AvailableCreditMemoListFields
      }
      restaurant_id
    }

    getUnpaidOrderInvoicesByCustomer(
      restaurant_id: $invoiceRestaurantFilter
      supplier_id: $supplierUUID
    ) {
      order_invoices {
        ...UnpaidInvoiceListFields
      }
      restaurant_id
      supplier_id
    }
  }
`;

export const GET_NEXT_AP_INFO_QUERY = gql`
  query AutoPaySettings_GetNextAutoPayInfo(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getNextAutopayInfo(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      auto_pay_settings {
        frequency
        lookback_period_days
        max_amount_in_micros
        uuid
      }
      amount_in_micros
      date
    }
  }
`;

export const REMOVE_PAYMENT_METHOD_MUTATION = gql`
  mutation PaymentForm_RemovePaymentMethod($paymentMethodUUID: uuid!) {
    removePaymentMethod(payment_method_id: $paymentMethodUUID) {
      payment_method {
        ...PaymentMethodFields
      }
    }
  }
  ${PAYMENT_METHOD_FIELDS_FRAGMENT}
`;

export const UNLOCK_EXPIRED_FTNI_SESSIONS = gql`
  mutation FNTI_UnlockExpiredFTNISessions(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    unlockExpiredFtniSessions(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      success
    }
  }
`;

export const SELECT_AUTO_PAY_SETTINGS_MUTATION = gql`
  mutation autopayContainer_SelectAutoPaySettings(
    $autoPaySettingUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    selectAutoPaySettings(
      auto_pay_settings_id: $autoPaySettingUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      auto_pay_settings_id
    }
  }
`;

const INITIAL_CONTEXT_VALUE: PaymentContextState = {
  autoShowModal: false,
  memo: '',
  selectedInvoiceUUIDs: new Set(),
  selectedPaymentMethod: null,
};

export const VIEWED_INVOICES_MAP_KEY = 'viewedInvoicesMap';

interface InvoiceMapType {
  invoiceUUIDs: string[];
  viewedDate: Moment;
}

export const PaymentsContext = createContext<PaymentsContextType | null>(null);

export enum PaymentsDataScope {
  All = 'ALL',
  None = 'NONE',
  PaymentMethods = 'PAYMENT_METHODS',
}

interface Props {
  children?: React.ReactNode;
  dataScope?: PaymentsDataScope;
}

export const PaymentsContextProvider: React.FC<Props> = ({
  children,
  dataScope = PaymentsDataScope.All,
}) => {
  const skipAllData = dataScope === PaymentsDataScope.None;
  const skipInvoiceAndAutoPayData = dataScope !== PaymentsDataScope.All;
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const [state, setState] = useState(INITIAL_CONTEXT_VALUE);
  const [restaurantsWithNewInvoices, setRestaurantsWithNewInvoices] = useState<
    Set<string>
  >(new Set());
  const enabledCustomerFacingPaymentMethods = useDynamicConfigValue(
    'enabled_customer_facing_payment_methods',
  );
  const tabCardConfig = useDynamicConfigValue('tab_card_config');

  const queryArgs = useMemo(() => {
    // Let the operator add payment methods even if they can't make in-app payments
    return {
      paymentMethodWhere: {
        _and: [
          { restaurant_uuid: { _eq: selectedRestaurantUUID } },
          { supplier_uuid: { _eq: supplierUUID } },
          { deleted_at: { _is_null: true } },
          ...(enabledCustomerFacingPaymentMethods?.includes(
            PaymentMethodType.PAD,
          )
            ? []
            : [{ payment_method_type: { _neq: PaymentMethodType.PAD } }]),
        ],
      },
      restaurantUUID: selectedRestaurantUUID,
      skipInvoiceAndAutoPayData,
      supplierUUID,
    };
  }, [
    enabledCustomerFacingPaymentMethods,
    selectedRestaurantUUID,
    skipInvoiceAndAutoPayData,
    supplierUUID,
  ]);

  const {
    data,
    loading,
    refetch: refetchPaymentContext,
  } = useWrappedQuery<
    Payments_PaymentContextQuery,
    Payments_PaymentContextQueryVariables
  >(PAYMENT_CONTEXT_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skipAllData,
    variables: queryArgs,
  });

  const [removePaymentMethodMutation] = useMutation<
    PaymentForm_RemovePaymentMethodMutation,
    PaymentForm_RemovePaymentMethodMutationVariables
  >(REMOVE_PAYMENT_METHOD_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(PAYMENT_CONTEXT_QUERY) as string],
  });

  const [unlockFtniInvoices, { loading: loadingUnlockFtniInvoices }] =
    useMutation<
      Fnti_UnlockExpiredFtniSessionsMutation,
      Fnti_UnlockExpiredFtniSessionsMutationVariables
    >(UNLOCK_EXPIRED_FTNI_SESSIONS);

  const {
    data: unpaidData,
    loading: unpaidLoading,
    networkStatus,
    refetch,
  } = useWrappedQuery<
    Payments_PaymentsContextUnpaidListQuery,
    Payments_PaymentsContextUnpaidListQueryVariables
  >(PAYMENTS_CONTEXT_UNPAID_LIST_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skipInvoiceAndAutoPayData,
    variables: {
      invoiceRestaurantFilter: supplierEmployeeRole
        ? selectedRestaurantUUID
        : undefined,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID,
      usableAfter: moment().startOf('day').toISOString(),
    },
  });

  const [selectAutoPaySettingsMutation, { loading: savingAutoPaySetting }] =
    useMutation<
      AutopayContainer_SelectAutoPaySettingsMutation,
      AutopayContainer_SelectAutoPaySettingsMutationVariables
    >(SELECT_AUTO_PAY_SETTINGS_MUTATION, {
      awaitRefetchQueries: true,
      refetchQueries: [
        getOperationName(PAYMENT_CONTEXT_QUERY) as string,
        getOperationName(GET_NEXT_AP_INFO_QUERY) as string,
      ],
    });

  // Only fetch credit memos for the selected restaurant, because they don't
  // add a bubble to the restaurant selector
  const availableCreditMemos =
    unpaidData?.getAvailableCreditMemosByCustomer?.[0]?.credit_memos;

  // We fetch all available invoices to add a bubble to the restaurant picker.
  // Find the unpaid invoices for the selected restaurant
  const unpaidInvoices = useMemo(() => {
    return unpaidData?.getUnpaidOrderInvoicesByCustomer.find(
      (invoicesByCustomer) => {
        // Nothing prevents a restaurant from being connected to multiple
        // suppliers of the same bizorg. Our user context prevents the user from
        // seeing the same restaurant in the location picker multiple times.
        // As a result, we may have multiple entries in the response with the
        // same restaurantUUID, but a different supplierUUID.
        // To be safe here, match unpaid invoices on both restaurantUUID and
        // supplierUUID so we view invoices for the correct chat.
        return (
          invoicesByCustomer.restaurant_id === selectedRestaurantUUID &&
          invoicesByCustomer.supplier_id === supplierUUID
        );
      },
    )?.order_invoices;
  }, [unpaidData, selectedRestaurantUUID, supplierUUID]);

  const supplier = data?.businesses_by_pk;

  const currencyCode = useMemo(() => {
    const countryCode = supplier?.address.country_code;
    return countryCode
      ? getCountryCurrencyCode(countryCode)
      : DEFAULT_CURRENCY_CODE;
  }, [supplier?.address.country_code]);

  const unpaidItems = useMemo(() => {
    // Display credits before invoices
    return [
      ...(availableCreditMemos || []).map(
        (
          creditMemo: AvailableCreditMemoListFieldsFragment,
        ): CreditMemoUnpaidItemType => {
          const creditedAt = creditMemo.credit_date
            ? new Date(creditMemo.credit_date)
            : null;
          return {
            amountInMicros: -creditMemo.credit_amount_in_micros,
            creditedAt,
            dateText: creditedAt
              ? i18n.t('issued_date', {
                  date: formatSummaryDateWithYear(new Date(creditedAt)),
                })
              : null,
            displayNumber: creditMemo.credit_number,
            type: PaymentItemType.Credit,
            uuid: creditMemo.credit_id,
          };
        },
      ),
      ...(unpaidInvoices || []).map(
        (inv: UnpaidInvoiceListFieldsFragment): InvoiceUnpaidItemType => {
          const orderFulfillmentAt = inv.order_fulfillment_at
            ? new Date(inv.order_fulfillment_at)
            : null;

          return {
            amountInMicros: inv.unpaid_amount_in_micros,
            dateText: orderFulfillmentAt
              ? i18n.t('fulfilled_date', {
                  date: formatSummaryDateWithYear(orderFulfillmentAt),
                })
              : null,
            displayNumber: inv.invoice_number,
            dueAt: inv.due_at ? new Date(inv.due_at) : null,
            orderFulfillmentAt,
            orderUUID: inv.order_id || null,
            type: PaymentItemType.Invoice,
            uuid: inv.invoice_id,
          };
        },
      ),
    ];
  }, [availableCreditMemos, unpaidInvoices]);

  const invoices = useMemo(() => {
    return unpaidItems.filter(
      (unpaidItem): unpaidItem is InvoiceUnpaidItemType =>
        unpaidItem.type === PaymentItemType.Invoice,
    );
  }, [unpaidItems]);

  useEffect(() => {
    if (skipInvoiceAndAutoPayData) {
      return;
    }
    (async () => {
      const today = moment();
      const tomorrow = moment().add(1, 'days');
      const yesterday = moment().subtract(1, 'days');
      const viewedInvoicesMap = await getViewedInvoicesMap();

      const nextRestaurantsWithInvoicesSet = new Set<string>();

      (unpaidData?.getUnpaidOrderInvoicesByCustomer || []).forEach(
        (invoicesByCustomer) => {
          const restaurantUUID = invoicesByCustomer.restaurant_id;
          if (!invoicesByCustomer.order_invoices.length) {
            return;
          }
          const viewedInvoicesSet = new Set(
            viewedInvoicesMap[restaurantUUID]?.invoiceUUIDs || [],
          );
          if (!viewedInvoicesSet.size) {
            nextRestaurantsWithInvoicesSet.add(restaurantUUID);
            return;
          }
          const notSeenToday = !moment(
            viewedInvoicesMap[restaurantUUID].viewedDate,
          ).isSame(today, 'day');

          if (
            invoicesByCustomer.order_invoices.some(
              (invoice) =>
                !viewedInvoicesSet.has(invoice.invoice_id) ||
                (notSeenToday &&
                  ((invoice.due_at !== null &&
                    moment(invoice.due_at).isSameOrAfter(yesterday, 'day') &&
                    moment(invoice.due_at).isSameOrBefore(tomorrow, 'day')) ||
                    (invoice.order_fulfillment_at !== null &&
                      moment(invoice.order_fulfillment_at).isSame(
                        yesterday,
                        'day',
                      )))),
            )
          ) {
            nextRestaurantsWithInvoicesSet.add(restaurantUUID);
          }
        },
      );

      setRestaurantsWithNewInvoices(nextRestaurantsWithInvoicesSet);
    })();
  }, [unpaidData, selectedRestaurantUUID, skipInvoiceAndAutoPayData]);

  const paymentMethods = useMemo(() => {
    return (data?.payment_methods || []).map<PaymentMethodOption>((pm) => ({
      adyenPaymentID: pm.payment_id,
      cashbackPercentage:
        pm.payment_method_type === PaymentMethodType.TAB &&
        tabCardConfig?.enabled
          ? (tabCardConfig?.cashback_percentage ?? undefined)
          : undefined,
      displayText: pm.display_text,
      isDefault: pm.is_default,
      isUnsaved: false,
      subtype: pm.payment_method_sub_type || '',
      type: pm.payment_method_type as PaymentMethodType,
      uuid: pm.uuid,
    }));
  }, [
    data?.payment_methods,
    tabCardConfig?.enabled,
    tabCardConfig?.cashback_percentage,
  ]);

  const enabledPaymentMethods = useMemo(() => {
    return (
      supplier?.supplier_chats[0]?.dynamic_config
        ?.enabled_customer_facing_payment_methods ??
      supplier?.supplier_metadata?.dynamic_config
        ?.enabled_customer_facing_payment_methods ??
      []
    );
  }, [supplier]);

  const transactionFeeMap: TransactionFeeMap = useMemo(
    () =>
      buildTransactionFeeMap(
        supplier?.supplier_metadata,
        supplier?.supplier_chats[0],
      ),
    [supplier],
  );

  const grossUpTransactionFeesCutoff =
    supplier?.supplier_metadata?.gross_up_transaction_fees_cutoff ?? null;
  const isGrossUpTransactionFee =
    !!grossUpTransactionFeesCutoff &&
    getTrueTime() >= new Date(grossUpTransactionFeesCutoff).getTime();

  const setPaymentContextState = useCallback(
    (values: Partial<PaymentContextState>) => {
      setState((prevState) => ({
        ...prevState,
        ...values,
      }));
    },
    [setState],
  );

  const resetContext = useCallback(() => {
    // Called after a successful payment. Clear the invoice selection (a partial
    // payment leaves the invoice in the unpaid list) and reset the payment
    // method in case it was an unsaved Adyen Dropin method.
    setPaymentContextState({
      memo: '',
      selectedInvoiceUUIDs: new Set(),
      selectedPaymentMethod: paymentMethods?.[0] || null,
    });
  }, [paymentMethods, setPaymentContextState]);

  // Update the selectedPaymentMethod to the default whenever the
  // list of paymentMethods changes
  useEffect(() => {
    if (paymentMethods?.length) {
      // Cannot rely on the sort order of paymentMethods, because the Apollo cache
      // may have changed outside of the Payments_PaymentContext query
      const defaultPaymentMethod = paymentMethods.find((pm) => pm.isDefault);

      setPaymentContextState({
        selectedPaymentMethod:
          // A default payment method should always exist, but fall back
          // to the first paymentMethod to be defensive
          defaultPaymentMethod || paymentMethods[0],
      });
    } else {
      // if empty, clear the selected payment method.
      setState((prevState) =>
        prevState.selectedPaymentMethod &&
        !prevState.selectedPaymentMethod.isUnsaved
          ? { ...prevState, selectedPaymentMethod: null }
          : prevState,
      );
    }
  }, [paymentMethods, setPaymentContextState]);

  const selectAllUnpaidItems = useCallback(() => {
    if (unpaidItems.length > 0) {
      setPaymentContextState({
        selectedInvoiceUUIDs: new Set(unpaidItems.map((i) => i.uuid)),
      });
    }
  }, [unpaidItems, setPaymentContextState]);

  const deselectAllUnpaidItems = useCallback(() => {
    setPaymentContextState({
      selectedInvoiceUUIDs: new Set(),
    });
  }, [setPaymentContextState]);

  const markInvoicesViewed = useCallback(
    async (invoiceUUIDs?: string[]) => {
      if (skipInvoiceAndAutoPayData) {
        return;
      }
      const viewedInvoicesMap = await getViewedInvoicesMap();
      viewedInvoicesMap[selectedRestaurantUUID] = {
        invoiceUUIDs: invoiceUUIDs || invoices.map((i) => i.uuid),
        viewedDate: moment(),
      };

      await AsyncStorage.setItem(
        VIEWED_INVOICES_MAP_KEY,
        JSON.stringify(viewedInvoicesMap),
      );

      setRestaurantsWithNewInvoices((prev) => {
        if (prev.has(selectedRestaurantUUID)) {
          prev.delete(selectedRestaurantUUID);
          return new Set(prev);
        } else {
          return prev;
        }
      });
    },
    [invoices, selectedRestaurantUUID, skipInvoiceAndAutoPayData],
  );

  const removePaymentMethod = useCallback(
    async (paymentMethodUUID: string) => {
      // have caller handle errors
      await removePaymentMethodMutation({
        variables: {
          paymentMethodUUID,
        },
      });

      if (state.selectedPaymentMethod?.uuid === paymentMethodUUID) {
        setPaymentContextState({
          selectedPaymentMethod: null,
        });
      }
    },
    [
      removePaymentMethodMutation,
      setPaymentContextState,
      state.selectedPaymentMethod,
    ],
  );

  const ftniEnabled =
    supplier?.supplier_chats[0]?.dynamic_config.payments_ftni ??
    supplier?.supplier_metadata?.dynamic_config.payments_ftni ??
    false;

  // If a distributor is on FTNI payments, we want to check and unlock any stale invoices when user reloads
  // This can happen when a payment session expires or the user closes the app after starting payment
  const hasUnlockedFtniInvoices = useRef(false);
  const unlockExpiredFtniInvoicesCallback = useCallback(async () => {
    if (ftniEnabled && !skipInvoiceAndAutoPayData) {
      await unlockFtniInvoices({
        variables: {
          restaurantUUID: selectedRestaurantUUID,
          supplierUUID,
        },
      });
    }
  }, [
    ftniEnabled,
    selectedRestaurantUUID,
    skipInvoiceAndAutoPayData,
    supplierUUID,
    unlockFtniInvoices,
  ]);

  useEffect(() => {
    if (skipInvoiceAndAutoPayData) {
      return;
    }
    (async () => {
      if (ftniEnabled && !hasUnlockedFtniInvoices.current) {
        await unlockExpiredFtniInvoicesCallback();
        await refetch();

        hasUnlockedFtniInvoices.current = true;
      }
    })();
  }, [
    ftniEnabled,
    refetch,
    skipInvoiceAndAutoPayData,
    unlockExpiredFtniInvoicesCallback,
  ]);

  const supplierAutoPaySettings = useMemo(
    () => data?.auto_pay_settings ?? [],
    [data?.auto_pay_settings],
  );
  const chatAutoPaySetting = useMemo(
    () =>
      supplier?.supplier_chats[0]?.chat_auto_pay_settings?.[0]
        ?.auto_pay_settings ?? null,
    [supplier?.supplier_chats],
  );

  const autoPayEnabled = useMemo(() => {
    const autoPayFeatureEnabled =
      supplier?.supplier_chats[0]?.dynamic_config.autopay_enabled ??
      supplier?.supplier_metadata?.dynamic_config.autopay_enabled ??
      false;

    return autoPayFeatureEnabled && supplierAutoPaySettings.length > 0;
  }, [
    supplier?.supplier_chats,
    supplier?.supplier_metadata,
    supplierAutoPaySettings,
  ]);

  const autoPaySelfServeEnabled =
    supplier?.supplier_chats[0]?.dynamic_config.autopay_self_serve_enabled ??
    supplier?.supplier_metadata?.dynamic_config.autopay_self_serve_enabled ??
    false;

  const selectAutoPaySettings = useCallback(
    async (autoPaySettingUUID: string) => {
      await selectAutoPaySettingsMutation({
        variables: {
          autoPaySettingUUID,
          restaurantUUID: selectedRestaurantUUID,
          supplierUUID,
        },
      });
    },
    [selectAutoPaySettingsMutation, selectedRestaurantUUID, supplierUUID],
  );

  const value = useMemo(() => {
    return {
      ...state,
      allUnpaidItems: unpaidItems,
      autoPayEnabled,
      autoPaySelfServeEnabled,
      chatAutoPaySetting,
      countryCode: supplier?.address.country_code ?? null,
      currencyCode,
      deselectAllUnpaidItems,
      enabledPaymentMethods,
      ftniEnabled: ftniEnabled,
      hasNewInvoices: restaurantsWithNewInvoices.has(selectedRestaurantUUID),
      isGrossUpTransactionFee,
      loading: loading || unpaidLoading || loadingUnlockFtniInvoices,
      markInvoicesViewed,
      paymentMethods,
      plaidEnabled:
        supplier?.supplier_chats[0]?.dynamic_config.plaid_enabled ??
        supplier?.supplier_metadata?.dynamic_config.plaid_enabled ??
        false,
      refetchPaymentContext,
      refreshing: networkStatus === NetworkStatus.refetch,
      refreshInvoices: async () => {
        if (skipInvoiceAndAutoPayData) {
          return;
        }
        const latestData = await refetch();

        const latestInvoicesForRestaurant =
          latestData.data.getUnpaidOrderInvoicesByCustomer.find(
            ({ restaurant_id }) => restaurant_id === selectedRestaurantUUID,
          )?.order_invoices ?? [];

        const latestCreditMemosForRestaurant =
          latestData.data.getAvailableCreditMemosByCustomer.find(
            ({ restaurant_id }) => restaurant_id === selectedRestaurantUUID,
          )?.credit_memos ?? [];

        // Mark invoices as viewed
        await markInvoicesViewed(
          latestInvoicesForRestaurant.map(({ invoice_id }) => invoice_id),
        );

        // Build a set of still-existing IDs
        const validIds = new Set([
          ...latestInvoicesForRestaurant.map(({ invoice_id }) => invoice_id),
          ...latestCreditMemosForRestaurant.map(({ credit_id }) => credit_id),
        ]);

        // Remove any selected invoice UUIDs that are no longer in the unpaid items
        setPaymentContextState({
          selectedInvoiceUUIDs: new Set(
            [...state.selectedInvoiceUUIDs].filter((id) => validIds.has(id)),
          ),
        });
      },
      removePaymentMethod,
      resetContext,
      restaurantAddress:
        supplier?.supplier_chats[0]?.restaurant?.address || null,
      restaurantsWithNewInvoices,
      savingAutoPaySetting,
      selectAllUnpaidItems,
      selectAutoPaySettings,
      setPaymentContextState,
      showChargebackDisclaimer:
        supplier?.supplier_metadata?.show_chargeback_disclaimer || false,
      supplierAutoPaySettings,
      transactionFeeMap,
      unlockExpiredFtniInvoices: unlockExpiredFtniInvoicesCallback,
      useV2Invoices: supplier?.supplier_metadata?.use_v2_invoices || false,
    };
  }, [
    autoPayEnabled,
    autoPaySelfServeEnabled,
    chatAutoPaySetting,
    currencyCode,
    deselectAllUnpaidItems,
    enabledPaymentMethods,
    ftniEnabled,
    loading,
    loadingUnlockFtniInvoices,
    markInvoicesViewed,
    networkStatus,
    paymentMethods,
    refetch,
    refetchPaymentContext,
    removePaymentMethod,
    resetContext,
    restaurantsWithNewInvoices,
    savingAutoPaySetting,
    selectAllUnpaidItems,
    selectAutoPaySettings,
    selectedRestaurantUUID,
    setPaymentContextState,
    skipInvoiceAndAutoPayData,
    state,
    supplier?.address.country_code,
    supplier?.supplier_chats,
    supplier?.supplier_metadata,
    supplierAutoPaySettings,
    isGrossUpTransactionFee,
    transactionFeeMap,
    unlockExpiredFtniInvoicesCallback,
    unpaidItems,
    unpaidLoading,
  ]);

  return (
    <PaymentsContext.Provider value={value}>
      {children}
    </PaymentsContext.Provider>
  );
};

export const usePaymentsContext = () => {
  const context = useContext(PaymentsContext);
  if (!context) {
    throw new Error('usePaymentsContext requires a PaymentsContextProvider');
  }

  return context;
};

export const useMaybePaymentsContext = () => useContext(PaymentsContext);

// Returns a map of chat UUID to viewed invoice UUIDs and the date viewed
const getViewedInvoicesMap = async (): Promise<{
  [restaurantUUID: string]: InvoiceMapType;
}> => {
  try {
    const value = await AsyncStorage.getItem(VIEWED_INVOICES_MAP_KEY);
    if (value) {
      return JSON.parse(value);
    }
  } catch (e) {
    captureException(e);
  }

  return {};
};
